import './ModalFormBuilder.scss';
import React, { FC, ReactNode, useRef, useState } from 'react';
import { Controller, FieldValues, UseFormReturn } from 'react-hook-form';
import clsx from 'clsx';
import Button from '@srm/ui/button';
import Modal from '@srm/ui/modal';
import NestedFormBuilder from '@molecules/formbuilder/NestedFormBuilder/NestedFormBuilder';
import { IChangeAction, IRows } from '@models/Forms/IForms';
import { IFieldElem } from '@models/IFormData';
import { IDocumentData } from '@models/document/IDocumentData';
import Hint from '@srm/ui/hint';
import { ModalSize } from '@srm/ui/types/ModalSize';
import { IAttachesCreateModel } from '@models/attaches/IAttachesCreateModel';
import { AttachFilesBlock, IAttachFilesBlockRefActions } from '@molecules/AttachFilesBlock/AttachFilesBlock';
import { v4 as uuidv4 } from 'uuid';
import { AddMailReminderBlock, IMailRemindersOptions } from '../AddMailReminderBlock/AddMailReminderBlock';
import { IMailRemindersBlockModel } from '@models/mailRemindersBlock/IMailRemindersBlockModel';
import { sendModalMsg } from '@srm/ui/lib/modalMessages';
import { ISelectedFiles } from '@molecules/AttachFilesBlock/ISelectedFiles';
import FormProviderControl from '@controls/FormProviderControl';
import HookFormProvider from '@srm/ui/controls/HookFormProvider';
import ModalLoadingOverlay from '@srm/ui/modal-loading-overlay';
import { CentralizedSideEffectsContext } from '@/context/Contexts';
import { stringifyError } from '@srm/core/errors';

export interface IModalFormBuilderProps {
    header: string;
    message?: string;
    size?: ModalSize;
    showOkButton: boolean;
    okButtonText?: string;
    showCancelButton: boolean;
    cancelButtonText?: string;
    onSubmit?: (data: FieldValues, files?: ISelectedFiles, mailReminderOptions?: IMailRemindersOptions) => void;
    onCancel?: () => void;
    loading?: boolean;
    /**
     * Отказ операции: строка либо то, чем отклонил интерцептор (массив строк с прикреплённым
     * кодом обращения). Десятки модалок кладут сюда `error` из catch как есть — разбирать и
     * дописывать код обращения обязан рендер, иначе он терялся при склейке в строку.
     */
    errorText?: unknown;
    successText?: string;
    warningText?: string;
    rows?: IRows;
    changeActions?: IChangeAction[];
    formMethods: UseFormReturn<IDocumentData>;
    fields: Record<string, IFieldElem>;
    children?: ReactNode;
    createAttachModel?: IAttachesCreateModel;
    mailRemindersBlockModel?: IMailRemindersBlockModel;
    showContent?: boolean;
    docId?: string;
    isEdit?: boolean;
    isNew?: boolean;
    submitProgress?: number;
    submitProgressLabel?: string;
    isSubmitting?: boolean;
    /**
     * Блокировать закрытие (крестик, Escape, клик снаружи и «Отмена»). По умолчанию — на всё
     * время `loading`/`isSubmitting`; модалка, у которой `loading` покрывает и загрузку формы,
     * передаёт сюда только выполнение — подготовку можно бросить закрытием окна.
     */
    closeDisabled?: boolean;
    /** Есть ли несохранённый ввод: Escape и щелчок по фону сначала спрашивают (`Modal.hasUnsavedInput`). */
    hasUnsavedInput?: () => boolean;
    /** Изменился набор файлов в блоке вложений (выбранный, но ещё не отправленный ввод). */
    onFilesChanged?: (files: ISelectedFiles) => void;
    className?: string;
    /**
     * Обёртка содержимого формы — например, окно ожидания фоновой задачи, которое сменяет поля ходом работы и итогом.
     * Элемент формы остаётся и без полей: кнопка подвала по-прежнему отправляет её, а `onSubmit` решает по фазе окна.
     */
    renderContent?: (content: ReactNode) => ReactNode;
}

const ModalFormBuilder: FC<IModalFormBuilderProps> = ({
    size = 'm',
    showContent = true,
    okButtonText = 'ОК',
    cancelButtonText = 'Отмена',
    isEdit = false,
    isNew = true,
    ...props
}: IModalFormBuilderProps) => {
    const childFilesBlock = useRef<IAttachFilesBlockRefActions | null>(null);
    const [filesSizeExceeded, setFilesSizeExceeded] = useState<boolean>(false);
    // Код обращения — последней строкой: Hint сохраняет переводы строк (pre-wrap).
    const errorMessage = props.errorText ? stringifyError(props.errorText) : '';

    const fieldsName: number[] = [];
    const idForm = uuidv4();

    const onSubmit = () => {
        const fields = props.formMethods.control._formValues.fields as FieldValues;
        let filesToSend: ISelectedFiles = { attachProperties: [], files: [] };
        if (props.createAttachModel && !childFilesBlock?.current?.isValid()) {
            return;
        }
        if (childFilesBlock?.current) {
            const f = childFilesBlock?.current.getData();
            if (f) filesToSend = f;
        }

        const mrOptions = props.formMethods.control._formValues.mailReminders;

        props.onSubmit && props.onSubmit(fields, filesToSend, mrOptions);
    };

    const showFooter = props.showOkButton || props.showCancelButton;
    const childContent = React.Children.toArray(props.children);
    const hasFormRows = (props.rows?.rows?.row?.length ?? 0) > 0;
    const hasFormContent =
        showContent &&
        (childContent.length > 0 ||
            hasFormRows ||
            Boolean(props.createAttachModel || props.mailRemindersBlockModel || props.renderContent));
    const hasValue = (value: unknown) => (typeof value === 'string' ? value.trim().length > 0 : Boolean(value));
    const hasFeedback = [props.errorText, props.warningText, props.successText, props.message].some(hasValue);
    const hasBody = Boolean(props.loading || typeof props.submitProgress === 'number' || hasFeedback || hasFormContent);
    const closeDisabled = props.closeDisabled ?? Boolean(props.loading || props.isSubmitting);
    const wrapContent = (content: ReactNode) => (props.renderContent ? props.renderContent(content) : content);

    return (
        <Modal
            className={clsx('modal-dlg-container', props.className)}
            size={size}
            onClose={props.onCancel}
            hasUnsavedInput={props.hasUnsavedInput}
            closeDisabled={closeDisabled}
            header={props.header}
            footer={
                showFooter ? (
                    <div className="modal-dlg-buttons">
                        {props.showOkButton && (
                            <Button
                                size="sm"
                                aria-label={okButtonText}
                                type={hasFormContent ? 'submit' : 'button'}
                                form={hasFormContent ? idForm : undefined}
                                onClick={hasFormContent ? undefined : onSubmit}
                                disabled={props.loading || filesSizeExceeded || props.isSubmitting}
                            >
                                {okButtonText}
                            </Button>
                        )}
                        {props.showCancelButton && (
                            <Button
                                variant="light"
                                size="sm"
                                aria-label={cancelButtonText}
                                onClick={() => {
                                    if (props?.onCancel) props?.onCancel();
                                }}
                                disabled={closeDisabled}
                            >
                                {cancelButtonText}
                            </Button>
                        )}
                    </div>
                ) : null
            }
        >
            {hasBody ? (
                <ModalLoadingOverlay
                    visible={!!props.loading}
                    progress={props.submitProgress}
                    progressLabel={props.submitProgressLabel}
                >
                    <div className="modal-dlg-container__form-wrapper">
                        {errorMessage && (
                            <Hint role="alert" title={`Ошибка: ${errorMessage}`} tone="danger" maxWidth="100%" />
                        )}
                        {props.warningText && (
                            <Hint
                                role="alert"
                                title={`Внимание: ${props.warningText}`}
                                tone="warning"
                                maxWidth="100%"
                            />
                        )}
                        {props.successText && (
                            <Hint role="alert" title={`${props.successText}`} tone="success" maxWidth="100%" />
                        )}
                        {props.message && <div className="modal-dlg-message">{props.message}</div>}

                        {hasFormContent && (
                            <CentralizedSideEffectsContext value={false}>
                                <FormProviderControl
                                    formMethods={props.formMethods}
                                    changeActions={props.changeActions}
                                    className={clsx('base-form-provider', 'doc-form')}
                                    onSubmit={onSubmit}
                                    id={idForm}
                                >
                                    <HookFormProvider controller={Controller}>
                                        {wrapContent(
                                            <>
                                                {props.children}
                                                {hasFormRows && (
                                                    <div className={clsx('pw-container-row', 'pw-content')}>
                                                        <NestedFormBuilder
                                                            fields={props.fields}
                                                            setError={(errors?: string[]) => {
                                                                if (errors) {
                                                                    sendModalMsg({
                                                                        message: errors,
                                                                        type: 'error',
                                                                    });
                                                                }
                                                            }}
                                                            rows={props.rows}
                                                            isEdit={isEdit}
                                                            isNew={isNew}
                                                            formMethods={props.formMethods}
                                                            includedFields={fieldsName}
                                                            docId={props.docId ? props.docId : ''}
                                                            uniqueKey={props.docId ? props.docId : ''}
                                                        />
                                                    </div>
                                                )}

                                                {props.createAttachModel && (
                                                    <AttachFilesBlock
                                                        ref={childFilesBlock}
                                                        model={props.createAttachModel}
                                                        onChanged={props.onFilesChanged}
                                                        onChangeFilesSizeExceeded={(value) => {
                                                            setFilesSizeExceeded(value);
                                                        }}
                                                    />
                                                )}

                                                {props.mailRemindersBlockModel && (
                                                    <AddMailReminderBlock
                                                        model={props.mailRemindersBlockModel}
                                                        formMethods={props.formMethods}
                                                    />
                                                )}
                                            </>,
                                        )}
                                    </HookFormProvider>
                                </FormProviderControl>
                            </CentralizedSideEffectsContext>
                        )}
                    </div>
                </ModalLoadingOverlay>
            ) : null}
        </Modal>
    );
};

export default ModalFormBuilder;
